/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import cx from 'classnames';
import { Popper, PopperHeader, PopperBody } from '@/components/AZCustomComponent/Popper/Popper';
import Image from '@/components/NextImage';
import { Button } from '@/components/Button';
import styles from './styles.module.scss';
import { Text, Actionable } from '@az/starc-ui';
import SignInModalV2 from '@/components/SignInModalV2';
import { useSignInFlowType } from '@/components/SignInFlowTypeContext';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import { useAppData } from '@/hooks/useAppData';

type RewardsInfoTooltipProps = {
  iconSrc: { alt: string; src: string };
  header: string;
  body: string;
  classes?: {
    icon?: string;
    container?: string;
    popper?: string;
    title?: string;
    body?: string;
  };
  'data-testid'?: string;
};

function TooltipIcon({ iconSrc }: { iconSrc: { alt: string; src: string } }) {
  return (
    <Image
      className={styles.tooltipImage}
      src={iconSrc.src}
      alt={iconSrc.alt}
      width={24}
      height={24}
    />
  );
}

export function RewardsInfoTooltip({
  iconSrc,
  header,
  body,
  classes = {},
  'data-testid': dataTestId,
}: RewardsInfoTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { showRewardSigninPopup } = useLoginConfirmation();
  const [open, setOpen] = useState(false);

  const { setFlowTypeFn, setIsSignInModalOpened } = useSignInFlowType();
  const { data: userData } = useMyProfile();
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const isStandaloneUser = userData?.userType === 4;
  const handleStandaloneRewards = (id?: string) => {
    if (id?.includes('Join')) {
      showRewardSigninPopup('SIGN_ME_UP_FORM');
      return;
    }

    showRewardSigninPopup('LINK_REWARDS_ACC');
  };

  const handleModalOpen = (id?: string): void => {
    closeTooltip();
    if (isStandaloneUser) {
      return handleStandaloneRewards(id);
    }

    setFlowTypeFn('login');
    setIsSignInModalOpened(true);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setIsSignInModalOpened(false);
  };

  const infoIconRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const openTooltip = () => {
    setIsOpen(true);
  };

  const closeTooltip = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOnMouseClick = () => {
    if (isOpen) {
      closeTooltip();
    } else {
      openTooltip();
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !infoIconRef?.current?.contains(event.target as Node)
      ) {
        closeTooltip();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter' || e.key === 'Escape') {
        closeTooltip();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeTooltip, isOpen]);

  const getAnchorEl = useCallback(() => {
    return infoIconRef.current;
  }, []);

  const applySignInLinkOnText = (text: string | undefined) => {
    if (!text) {
      return null;
    }
    if (userAuthenticated && !isStandaloneUser) {
      return (
        <Text variant="subtitle-regular" className={styles.rewardsBodyText}>
          † {text}
        </Text>
      );
    }
    const signInText = 'sign in or create an account';
    const [start, end] = text.split(signInText);

    const actionableSignInText = (
      <Actionable onClick={() => handleModalOpen()} className={styles.signInLink}>
        {signInText}
      </Actionable>
    );

    return (
      <Text variant="subtitle-regular" className={styles.rewardsBodyText}>
        † {start} {actionableSignInText} {end}
      </Text>
    );
  };

  return (
    <div ref={infoIconRef} className={cx(styles.toolTipContainer, classes.container)}>
      <Button
        data-testid={dataTestId}
        aria-haspopup={true}
        onFocus={openTooltip}
        onMouseDown={handleOnMouseClick}
        type="button"
        customClass={cx(styles.toolTipIcon, {
          [classes.icon || '']: classes.icon,
        })}
        variant="ghost"
      >
        <TooltipIcon iconSrc={iconSrc} />
      </Button>
      {isOpen && (
        <Popper
          isOpen={isOpen}
          anchorEl={getAnchorEl}
          paperRef={popperRef}
          placement="bottom"
          classes={{
            popper: cx(styles.toolTipPopperContainer, styles.popperPadding, {
              [classes.popper || '']: classes.popper,
            }),
            paper: styles.toolTipPopperPaper,
          }}
          elevation={4}
        >
          <PopperHeader
            classes={{
              title: classes.title || styles.toolTipPopperTitle,
              heading: styles.toolTipPopperHeading,
            }}
            handleClose={closeTooltip}
            hideCloseIcon={false}
          >
            <h1>
              <strong></strong>
            </h1>
          </PopperHeader>
          <PopperBody
            classes={{
              body: classes.body,
            }}
          >
            <div className={styles.rewardsBody}>
              <Text variant="small-body" className={styles.rewardsHeader}>
                {header}
              </Text>
              {applySignInLinkOnText(body)}
            </div>
          </PopperBody>
        </Popper>
      )}
      <SignInModalV2 isOpen={open} onClose={handleModalClose} />
    </div>
  );
}
